/**
 * @fileoverview Compact Meta Ads connect/sync card for embedding on data
 * surfaces (first consumer: Reports → Advanced filters). Shows a connect CTA
 * while the project has no Meta connection, and a one-line sync affordance
 * once connected, so campaign spend can be brought next to funnel results
 * without leaving the page. OAuth returns to the current page (the hook sends
 * window.location as returnTo).
 *
 * Long sync windows are split into 90-day chunks: Meta rejects long ad-level
 * insight windows outright ("reduce the amount of data") and caps history at
 * 37 months (#3018), so "All available" stays just inside that limit.
 */

import React from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useMetaAdsConnect } from '../hooks/useMetaAdsConnect';
import { getMetaAdsConnectService } from '../services/MetaAdsConnectService';
import { META_ADS_INLINE_CARD_TEST_IDS } from '../testIds';

export { META_ADS_INLINE_CARD_TEST_IDS } from '../testIds';

const MS_PER_DAY = 24 * 60 * 60 * 1000;
// Meta rejects long ad-level windows on large accounts — sync in safe chunks.
const SYNC_CHUNK_DAYS = 90;

export const SYNC_PERIOD_OPTIONS = [
  { value: '90d', label: 'Last 90 days', days: 90 },
  { value: '12m', label: 'Last 12 months', days: 365 },
  { value: '36m', label: 'All available (~3 years)', days: 1095 },
] as const;

type SyncPeriodValue = (typeof SYNC_PERIOD_OPTIONS)[number]['value'];

function isoDate(timestampMs: number): string {
  return new Date(timestampMs).toISOString().slice(0, 10);
}

export interface MetaAdsInlineConnectCardProps {
  projectId: string;
}

export function MetaAdsInlineConnectCard({ projectId }: MetaAdsInlineConnectCardProps): React.ReactElement | null {
  const {
    status,
    isConnected,
    loading,
    actionLoading,
    connect,
    adAccounts,
    accountsLoading,
    refreshAdAccounts,
    error,
  } = useMetaAdsConnect(projectId);
  const [selectedAccountId, setSelectedAccountId] = React.useState('');
  const [syncPeriod, setSyncPeriod] = React.useState<SyncPeriodValue>('90d');
  const [syncing, setSyncing] = React.useState(false);
  const [syncProgress, setSyncProgress] = React.useState<string | null>(null);
  const [syncedRows, setSyncedRows] = React.useState<number | null>(null);
  const [syncError, setSyncError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (isConnected && adAccounts.length === 0 && !accountsLoading) {
      void refreshAdAccounts();
    }
  }, [isConnected, adAccounts.length, accountsLoading, refreshAdAccounts]);

  React.useEffect(() => {
    if (!selectedAccountId && adAccounts.length > 0) {
      setSelectedAccountId(adAccounts[0].id);
    }
  }, [adAccounts, selectedAccountId]);

  if (loading || !status) return null;
  if (!isConnected && status.providerConfigured === false) return null;

  if (!isConnected) {
    return (
      <Paper
        variant="outlined"
        data-testid={META_ADS_INLINE_CARD_TEST_IDS.connectCard}
        sx={{ p: 2, borderStyle: 'dashed', borderColor: theme => theme.palette.divider }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <CampaignIcon color="action" />
          <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
            Connect Meta Ads to see campaign spend, CAC and match quality next to your funnel results.
          </Typography>
          <Button
            data-testid={META_ADS_INLINE_CARD_TEST_IDS.connectButton}
            variant="contained"
            size="small"
            startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : <CampaignIcon />}
            onClick={connect}
            disabled={actionLoading}
          >
            {actionLoading ? 'Connecting…' : 'Connect Meta Ads'}
          </Button>
        </Stack>
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      </Paper>
    );
  }

  const handleSync = async () => {
    if (!selectedAccountId || syncing) return;
    const period = SYNC_PERIOD_OPTIONS.find(option => option.value === syncPeriod) ?? SYNC_PERIOD_OPTIONS[0];
    setSyncing(true);
    setSyncError(null);
    setSyncedRows(null);
    try {
      const service = getMetaAdsConnectService();
      setSyncProgress('Syncing campaign metadata…');
      await service.syncMetadata({ projectId, adAccountId: selectedAccountId });

      const untilMs = Date.now();
      const sinceMs = untilMs - period.days * MS_PER_DAY;
      const chunks: Array<[string, string]> = [];
      for (let start = sinceMs; start < untilMs; start += SYNC_CHUNK_DAYS * MS_PER_DAY) {
        const end = Math.min(start + SYNC_CHUNK_DAYS * MS_PER_DAY, untilMs);
        chunks.push([isoDate(start), isoDate(end)]);
      }

      let totalRows = 0;
      for (let index = 0; index < chunks.length; index += 1) {
        setSyncProgress(chunks.length > 1
          ? `Syncing delivery window ${index + 1}/${chunks.length}…`
          : 'Syncing delivery…');
        const [since, until] = chunks[index];
        const result = await service.syncDelivery({ projectId, adAccountId: selectedAccountId, since, until });
        totalRows += result.deliveryRowsWritten ?? result.rowsWritten ?? 0;
      }
      setSyncedRows(totalRows);
    } catch (syncFailure) {
      setSyncError(syncFailure instanceof Error ? syncFailure.message : 'Sync failed');
    } finally {
      setSyncing(false);
      setSyncProgress(null);
    }
  };

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={1.5}
      alignItems={{ md: 'center' }}
      data-testid={META_ADS_INLINE_CARD_TEST_IDS.connectCard}
    >
      <Stack direction="row" spacing={0.75} alignItems="center">
        <CheckCircleOutlineIcon color="success" fontSize="small" />
        <Typography variant="caption" color="text.secondary">
          Meta Ads connected{status.displayName ? ` — ${status.displayName}` : ''}
        </Typography>
      </Stack>
      <TextField
        data-testid={META_ADS_INLINE_CARD_TEST_IDS.accountSelect}
        select
        size="small"
        label="Ad account"
        value={selectedAccountId}
        onChange={event => setSelectedAccountId(event.target.value)}
        disabled={accountsLoading || adAccounts.length === 0}
        sx={{ minWidth: 220 }}
      >
        {adAccounts.map(account => (
          <MenuItem key={account.id} value={account.id}>
            {account.name || account.accountId || account.id}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        data-testid={META_ADS_INLINE_CARD_TEST_IDS.periodSelect}
        select
        size="small"
        label="Period"
        value={syncPeriod}
        onChange={event => setSyncPeriod(event.target.value as SyncPeriodValue)}
        disabled={syncing}
        sx={{ minWidth: 190 }}
      >
        {SYNC_PERIOD_OPTIONS.map(option => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
      <Button
        data-testid={META_ADS_INLINE_CARD_TEST_IDS.syncButton}
        size="small"
        variant="outlined"
        onClick={handleSync}
        disabled={syncing || !selectedAccountId}
        startIcon={syncing ? <CircularProgress size={14} color="inherit" /> : undefined}
      >
        {syncing ? (syncProgress ?? 'Syncing…') : 'Sync'}
      </Button>
      {syncedRows !== null && !syncing && (
        <Typography variant="caption" color="success.main">
          Synced {syncedRows} delivery rows — refresh the report to see them.
        </Typography>
      )}
      {(syncError || error) && !syncing && (
        <Typography variant="caption" color="error.main">{syncError || error}</Typography>
      )}
    </Stack>
  );
}

export default MetaAdsInlineConnectCard;
